import React, { useState, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Table,
  Alert,
  Progress,
} from "reactstrap";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import Loader from "Components/Common/Loader";
import {
  useGetProjectBudgetsByProjectIdQuery,
  useCreateProjectBudgetMutation,
  useUpdateProjectBudgetMutation,
  useDeleteProjectBudgetMutation,
  useBulkImportProjectBudgetMutation,
} from "slices/projects/projectSlice";
import { toast } from "react-toastify";
import { getToken, getUserRole } from "helpers/jwt-token-access/auth-token-header";

interface ProjectBudgetTabProps {
  projectId: string | undefined;
  projectData: any;
}

const statusOptions = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "closed", label: "Closed" },
  { value: "over_budget", label: "Over Budget" },
];

const sectionSchema = Yup.object().shape({
  section_name: Yup.string().required("Section name is required").trim(),
  allocated_cost: Yup.number()
    .required("Allocated cost is required")
    .min(0, "Must be 0 or greater"),
  description: Yup.string().nullable(),
  status: Yup.string().oneOf(["draft", "active", "closed", "over_budget"]).default("draft"),
});

const ProjectBudgetTab: React.FC<ProjectBudgetTabProps> = ({
  projectId,
  projectData,
}) => {
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [sectionExportId, setSectionExportId] = useState<number | null>(null);
  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useGetProjectBudgetsByProjectIdQuery(
    { projectId: projectId!, page, pageSize, search },
    { skip: !projectId }
  );

  const budgets = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = data?.total_page ?? 1;
  const currentPage = data?.current_page ?? 1;

  const [createBudget, { isLoading: isCreating }] = useCreateProjectBudgetMutation();
  const [updateBudget, { isLoading: isUpdating }] = useUpdateProjectBudgetMutation();
  const [deleteBudget, { isLoading: isDeleting }] = useDeleteProjectBudgetMutation();
  const [bulkImportBudget, { isLoading: isImporting }] = useBulkImportProjectBudgetMutation();

  const handleDownloadTemplate = useCallback(async () => {
    const apiBase =
      (typeof window !== "undefined" &&
        (window as any).__RUNTIME_CONFIG__?.REACT_APP_API_URL) ||
      "";
    try {
      const token = getToken();
      const userRole = getUserRole();
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (userRole) headers["AccessRole"] = userRole;
      const res = await fetch(
        `${apiBase}organization/api/v1/project-budgets/download-template/`,
        { headers, credentials: "same-origin" }
      );
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "budget_sections_import_template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template");
    }
  }, []);

  const handleBulkImport = useCallback(async () => {
    if (!projectId || !importFile) return;
    setImportError(null);
    try {
      const res: any = await bulkImportBudget({ project: projectId, file: importFile });
      if (res?.error) {
        const data = res.error?.data;
        const msg =
          data?.error ||
          (data?.missing_columns
            ? `Missing columns: ${data.missing_columns.join(", ")}`
            : data?.row_errors
            ? `Row ${data.row_errors[0].row}: ${data.row_errors[0].error}`
            : "Import failed");
        setImportError(msg);
        return;
      }
      toast.success(res?.data?.message || "Budget sections imported successfully");
      setImportModal(false);
      setImportFile(null);
      setImportError(null);
      refetch();
    } catch (e) {
      setImportError("Import failed");
    }
  }, [projectId, importFile, bulkImportBudget, refetch]);

  const handleExport = useCallback(
    async (type: "csv" | "excel") => {
      const apiBase =
        (typeof window !== "undefined" &&
          (window as any).__RUNTIME_CONFIG__?.REACT_APP_API_URL) ||
        "";
      if (!apiBase || !projectId) return;
      setExportLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("project", String(projectId));
        params.set("type", type);
        // Fields must match helpers.export_valid_field_constant.project_budget_export_fields
        // Include project estimation cost and section-level PO allocation totals
        params.set(
          "export",
          "[project__project_name,project__project_estimation_cost,section_name,allocated_cost,actual_cost,remaining_cost,allocations_mapped_total,allocations_actual_total,status,description,created_at,modified_at]"
        );
        // Use project name as file name where possible
        if (projectData?.project_name) {
          params.set("filename", projectData.project_name);
        } else {
          params.set("filename", `project_${projectId}_budgets`);
        }

        const url = `${apiBase}organization/api/v1/project-budgets/?${params.toString()}`;

        const token = getToken();
        const userRole = getUserRole();
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        if (userRole) headers["AccessRole"] = userRole;

        const res = await fetch(url, { headers, credentials: "same-origin" });
        if (!res.ok) {
          throw new Error(`Export failed with status ${res.status}`);
        }
        const blob = await res.blob();
        const ext = type === "csv" ? "csv" : "xlsx";
        const fileURL = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = fileURL;
        link.download = `${projectData?.project_name || `project_${projectId}_budgets`}.${ext}`;
        link.click();
        window.URL.revokeObjectURL(fileURL);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Project budget export failed", err);
        toast.error("Failed to export project budgets.");
      } finally {
        setExportLoading(false);
      }
    },
    [projectId, projectData?.project_name]
  );

  const handleSectionExport = useCallback(
    async (section: any) => {
      if (!projectId || !section?.id) return;
      const apiBase =
        (typeof window !== "undefined" &&
          (window as any).__RUNTIME_CONFIG__?.REACT_APP_API_URL) ||
        "";
      if (!apiBase) return;
      setSectionExportId(section.id);
      try {
        const params = new URLSearchParams();
        params.set("project_budget", String(section.id));
        params.set("type", "excel");
        // Fields must match project_budget_allocation_export_fields
        params.set(
          "export",
          "[project_budget__project__project_name,project_budget__section_name,purchase_order__inventory_purchase_order_number,purchase_order__total_po_cost,purchase_order__po_status,mapped_cost,actual_cost,status,created_at,modified_at]"
        );
        const baseName = `${projectData?.project_name || `project_${projectId}`}_${section.section_name || "section"}`;
        params.set("filename", baseName);

        const url = `${apiBase}organization/api/v1/project-budget-cost-mappings/?${params.toString()}`;

        const token = getToken();
        const userRole = getUserRole();
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        if (userRole) headers["AccessRole"] = userRole;

        const res = await fetch(url, { headers, credentials: "same-origin" });
        if (!res.ok) {
          throw new Error(`Export failed with status ${res.status}`);
        }
        const blob = await res.blob();
        const fileURL = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = fileURL;
        link.download = `${baseName}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(fileURL);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Section budget export failed", err);
        toast.error("Failed to export section purchase orders.");
      } finally {
        setSectionExportId(null);
      }
    },
    [projectId, projectData?.project_name]
  );

  const addFormik = useFormik({
    initialValues: {
      section_name: "",
      allocated_cost: "",
      description: "",
      status: "draft",
    },
    validationSchema: sectionSchema,
    onSubmit: async (values) => {
      if (!projectId) return;
      try {
        await createBudget({
          project: parseInt(projectId, 10),
          section_name: values.section_name.trim(),
          allocated_cost: values.allocated_cost,
          description: values.description?.trim() || undefined,
          status: values.status,
        }).unwrap();
        toast.success("Budget section added.");
        setAddModal(false);
        addFormik.resetForm();
        refetch();
      } catch (err: any) {
        toast.error(err?.data?.allocated_cost?.[0] || err?.data?.detail || "Failed to add section.");
      }
    },
  });

  const editFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      section_name: selectedBudget?.section_name ?? "",
      allocated_cost: selectedBudget?.allocated_cost ?? "",
      description: selectedBudget?.description ?? "",
      status: selectedBudget?.status ?? "draft",
    },
    validationSchema: sectionSchema,
    onSubmit: async (values) => {
      if (!selectedBudget?.id) return;
      try {
        await updateBudget({
          id: selectedBudget.id,
          data: {
            section_name: values.section_name.trim(),
            allocated_cost: String(values.allocated_cost),
            description: values.description?.trim() || undefined,
            status: values.status,
          },
        }).unwrap();
        toast.success("Budget section updated.");
        setEditModal(false);
        setSelectedBudget(null);
        refetch();
      } catch (err: any) {
        toast.error(err?.data?.allocated_cost?.[0] || err?.data?.detail || "Failed to update.");
      }
    },
  });

  const openEdit = (row: any) => {
    setSelectedBudget(row);
    editFormik.setValues({
      section_name: row.section_name ?? "",
      allocated_cost: row.allocated_cost ?? "",
      description: row.description ?? "",
      status: row.status ?? "draft",
    });
    setEditModal(true);
  };

  const openDelete = (row: any) => {
    setSelectedBudget(row);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedBudget?.id) return;
    try {
      await deleteBudget(selectedBudget.id).unwrap();
      toast.success("Budget section removed.");
      setDeleteModal(false);
      setSelectedBudget(null);
      refetch();
    } catch {
      toast.error("Failed to delete section.");
    }
  };

  const estimationCost = projectData?.project_estimation_cost
    ? parseFloat(String(projectData.project_estimation_cost).replace(/,/g, "")) || 0
    : 0;
  const totalAllocated = Array.isArray(budgets)
    ? budgets.reduce((sum: number, b: any) => sum + (parseFloat(b.allocated_cost) || 0), 0)
    : 0;
  const totalActual = Array.isArray(budgets)
    ? budgets.reduce((sum: number, b: any) => sum + (parseFloat(b.actual_cost) || 0), 0)
    : 0;

  if (!projectId) return null;

  return (
    <>
      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h5 className="card-title mb-0">Project Budget Sections</h5>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Input
                  type="search"
                  placeholder="Search sections..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="form-control form-control-sm"
                  style={{ maxWidth: "260px" }}
                />
                <Button
                  color="secondary"
                  size="sm"
                  disabled={exportLoading || !budgets.length}
                  onClick={() => handleExport("excel")}
                >
                  <i className="ri-download-line align-bottom me-1" />
                  {exportLoading ? "Exporting…" : "Export"}
                </Button>
                <Button
                  color="info"
                  size="sm"
                  outline
                  onClick={() => setImportModal(true)}
                >
                  <i className="ri-upload-2-line align-bottom me-1" />
                  Import
                </Button>
                <Button color="primary" size="sm" onClick={() => setAddModal(true)}>
                  <i className="ri-add-line align-bottom me-1" />
                  Add Section
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {/* Summary */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="border-0 shadow-sm mb-0 bg-primary">
                    <CardBody className="p-3 text-white">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <small className="opacity-75 d-block mb-1 text-white">
                            <i className="ri-draft-line align-middle me-1" /> Estimation Cost
                          </small>
                          <h4 className="mb-0 fw-bold text-white">{isNaN(estimationCost) ? projectData?.project_estimation_cost ?? "—" : estimationCost.toFixed(2)}</h4>
                        </div>
                        <div className="fs-2 opacity-50 text-white">
                          <i className="ri-file-list-3-line" />
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-0 shadow-sm mb-0 bg-info">
                    <CardBody className="p-3 text-white">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <small className="opacity-75 d-block mb-1 text-white">
                            <i className="ri-price-tag-3-line align-middle me-1" /> Total Allocated
                          </small>
                          <h4 className="mb-0 fw-bold text-white">{totalAllocated.toFixed(2)}</h4>
                        </div>
                        <div className="fs-2 opacity-50 text-white">
                          <i className="ri-wallet-3-line" />
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-0 shadow-sm mb-0 bg-warning">
                    <CardBody className="p-3 text-white">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <small className="opacity-75 d-block mb-1 text-white">
                            <i className="ri-wallet-3-line align-middle me-1" /> Total Actual
                          </small>
                          <h4 className="mb-0 fw-bold text-white">{totalActual.toFixed(2)}</h4>
                        </div>
                        <div className="fs-2 opacity-50 text-white">
                          <i className="ri-money-dollar-circle-line" />
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-0 shadow-sm mb-0 bg-success">
                    <CardBody className="p-3 text-white">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <small className="opacity-75 d-block mb-1 text-white">
                            <i className="ri-wallet-2-line align-middle me-1" /> Total Remaining
                          </small>
                          <h4 className="mb-0 fw-bold text-white">{(estimationCost - totalActual).toFixed(2)}</h4>
                        </div>
                        <div className="fs-2 opacity-50 text-white">
                          <i className="ri-bank-line" />
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Budget Utilization Alert */}
              {estimationCost > 0 && (
                <>
                  {totalActual > estimationCost && (
                    <Alert color="danger" className="d-flex align-items-center mb-3">
                      <i className="ri-error-warning-line fs-4 me-2" />
                      <div className="flex-grow-1">
                        <strong>Budget Exceeded!</strong> Total actual cost ({totalActual.toFixed(2)}) exceeds estimation cost ({estimationCost.toFixed(2)}) by {(totalActual - estimationCost).toFixed(2)}.
                      </div>
                    </Alert>
                  )}
                  {totalActual / estimationCost >= 0.9 && totalActual <= estimationCost && (
                    <Alert color="warning" className="d-flex align-items-center mb-3">
                      <i className="ri-alert-line fs-4 me-2" />
                      <div className="flex-grow-1">
                        <strong>Budget Warning:</strong> {((totalActual / estimationCost) * 100).toFixed(1)}% of budget utilized. {(estimationCost - totalActual).toFixed(2)} remaining.
                      </div>
                    </Alert>
                  )}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between small text-muted mb-1">
                      <span>Budget Utilization</span>
                      <span>{((totalActual / estimationCost) * 100).toFixed(1)}%</span>
                    </div>
                    <Progress
                      value={(totalActual / estimationCost) * 100}
                      color={totalActual > estimationCost ? "danger" : totalActual / estimationCost >= 0.9 ? "warning" : "success"}
                      className="mb-0"
                      style={{ height: "8px" }}
                    />
                  </div>
                </>
              )}

              
            </CardBody>
          </Card>

          {isLoading ? (
                <Loader />
              ) : (
                <>
                    <Row>
                    {Array.isArray(budgets) && budgets.length > 0 ? (
                      budgets?.map((section: any) => {
                        const allocated = parseFloat(section.allocated_cost || 0);
                        const actual = parseFloat(section.actual_cost || 0);
                        const remaining = parseFloat(section.remaining_cost || 0);
                        const utilizationPercent = allocated > 0 ? (actual / allocated) * 100 : 0;
                        const isOverBudget = actual > allocated || section.status === "over_budget";
                        const isNearLimit = utilizationPercent >= 90 && actual <= allocated;

                        return (
                          <Col key={section.id} xs={12} className="mb-3">
                            <Card className={`border shadow-sm mb-0 ${isOverBudget ? 'border-danger' : isNearLimit ? 'border-warning' : ''}`}>
                              <CardHeader className={`py-3 d-flex align-items-center justify-content-between flex-wrap gap-2 ${isOverBudget ? 'bg-danger bg-opacity-10' : isNearLimit ? 'bg-warning bg-opacity-10' : ''}`}>
                                <div className="d-flex align-items-center gap-2">
                                  <h5 className="mb-0">{section.section_name}</h5>
                                  {isOverBudget && (
                                    <Badge color="danger" className="d-flex align-items-center gap-1">
                                      <i className="ri-error-warning-line" />
                                      Over Budget
                                    </Badge>
                                  )}
                                  {isNearLimit && !isOverBudget && (
                                    <Badge color="warning" className="d-flex align-items-center gap-1">
                                      <i className="ri-alert-line" />
                                      Near Limit
                                    </Badge>
                                  )}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <Badge
                                    color={
                                      section.status === "active"
                                        ? "success"
                                        : section.status === "closed"
                                          ? "secondary"
                                          : section.status === "over_budget"
                                            ? "danger"
                                            : "warning"
                                    }
                                  >
                                    {section.status?.replace("_", " ") || "—"}
                                  </Badge>
                                  <div className="hstack gap-1">
                                    <Button
                                      color="soft-primary"
                                      size="sm"
                                      className="btn-icon"
                                      onClick={() => openEdit(section)}
                                      title="Edit"
                                    >
                                      <i className="ri-pencil-line" />
                                    </Button>
                                    <Button
                                      color="soft-success"
                                      size="sm"
                                      className="btn-icon"
                                      onClick={() => handleSectionExport(section)}
                                      title="Export section POs"
                                      disabled={sectionExportId === section.id}
                                    >
                                      {sectionExportId === section.id ? (
                                        <i className="ri-loader-4-line spin" />
                                      ) : (
                                        <i className="ri-download-line" />
                                      )}
                                    </Button>
                                    <Button
                                      color="soft-danger"
                                      size="sm"
                                      className="btn-icon"
                                      onClick={() => openDelete(section)}
                                      title="Delete"
                                      disabled
                                    >
                                      <i className="ri-delete-bin-line" />
                                    </Button>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardBody className="py-3">
                                {section.description && (
                                  <p className="small text-muted mb-3">{section.description}</p>
                                )}

                                {/* Warnings */}
                                {isOverBudget && (
                                  <Alert color="danger" className="mb-3 py-2">
                                    <div className="d-flex align-items-start">
                                      <i className="ri-error-warning-line fs-5 me-2 mt-1" />
                                      <div>
                                        <strong>Budget Exceeded!</strong> Actual cost ({actual.toFixed(2)}) exceeds allocated budget ({allocated.toFixed(2)}) by {(actual - allocated).toFixed(2)}.
                                      </div>
                                    </div>
                                  </Alert>
                                )}
                                {isNearLimit && !isOverBudget && (
                                  <Alert color="warning" className="mb-3 py-2">
                                    <div className="d-flex align-items-start">
                                      <i className="ri-alert-line fs-5 me-2 mt-1" />
                                      <div>
                                        <strong>Budget Warning:</strong> {utilizationPercent.toFixed(1)}% of allocated budget utilized. Only {remaining.toFixed(2)} remaining.
                                      </div>
                                    </div>
                                  </Alert>
                                )}

                                <Row className="mb-3">
                                  <Col md={4}>
                                    <Card className="border-0 bg-light mb-0">
                                      <CardBody className="p-3">
                                        <small className="text-muted d-block mb-2">
                                          <i className="ri-price-tag-3-line align-middle me-1" /> Allocated
                                        </small>
                                        <h5 className="mb-0 fw-bold">{allocated.toFixed(2)}</h5>
                                      </CardBody>
                                    </Card>
                                  </Col>
                                  <Col md={4}>
                                    <Card className="border-0 bg-light mb-0">
                                      <CardBody className="p-3">
                                        <small className="text-muted d-block mb-2">
                                          <i className="ri-wallet-3-line align-middle me-1" /> Actual
                                        </small>
                                        <h5 className={`mb-0 fw-bold ${isOverBudget ? 'text-danger' : ''}`}>{actual.toFixed(2)}</h5>
                                      </CardBody>
                                    </Card>
                                  </Col>
                                  <Col md={4}>
                                    <Card className="border-0 bg-light mb-0">
                                      <CardBody className="p-3">
                                        <small className="text-muted d-block mb-2">
                                          <i className="ri-wallet-2-line align-middle me-1" /> Remaining
                                        </small>
                                        <h5 className={`mb-0 fw-bold ${remaining > 0 ? 'text-success' : 'text-danger'}`}>{remaining.toFixed(2)}</h5>
                                      </CardBody>
                                    </Card>
                                  </Col>
                                </Row>

                                {/* Progress Bar */}
                                {allocated > 0 && (
                                  <div className="mb-3">
                                    <div className="d-flex justify-content-between small text-muted mb-1">
                                      <span>Section Utilization</span>
                                      <span>{utilizationPercent.toFixed(1)}%</span>
                                    </div>
                                    <Progress
                                      value={utilizationPercent}
                                      color={isOverBudget ? "danger" : isNearLimit ? "warning" : "success"}
                                      className="mb-0"
                                      style={{ height: "8px" }}
                                    />
                                  </div>
                                )}
                              <div className="fw-semibold mb-2 d-flex align-items-center gap-2">
                                <i className="ri-shopping-cart-line" />
                                Purchase orders
                                {section.allocations && section.allocations.length > 0 && (
                                  <Badge color="primary" className="rounded-pill">{section.allocations.length}</Badge>
                                )}
                              </div>
                              {section.allocations && section.allocations.length > 0 ? (
                                <div className="table-responsive">
                                  <Table className="table-bordered table-sm mb-0 align-middle">
                                    <thead className="table-light">
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Supplier</th>
                                        <th>Contact</th>
                                        <th>PO Total</th>
                                        
                                        <th>PO Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {section.allocations.map((allocation: any) => (
                                        <tr key={allocation.id}>
                                          <td>
                                            {allocation.purchase_order ? (
                                              <Link to={`/purchase-order-detail/${allocation.purchase_order}`}>
                                                {allocation.purchase_order_number || `PO #${allocation.purchase_order}`}
                                              </Link>
                                            ) : (
                                              allocation.purchase_order_number || `PO #${allocation.purchase_order}`
                                            )}
                                          </td>
                                          <td>{allocation.supplier_name || "—"}</td>
                                          <td className="small">
                                            {allocation.supplier_email && (
                                              <a href={`mailto:${allocation.supplier_email}`}>{allocation.supplier_email}</a>
                                            )}
                                            {allocation.supplier_phone && (
                                              <span className="d-block">{allocation.supplier_phone}</span>
                                            )}
                                            {!allocation.supplier_email && !allocation.supplier_phone && "—"}
                                          </td>
                                          <td>{parseFloat(allocation.purchase_order_total || 0).toFixed(2)}</td>
                                          
                                          <td>
                                            <Badge color="light" className="text-dark">
                                              {allocation.po_status || "—"}
                                            </Badge>
                                          </td>
                                          
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              ) : (
                                <p className="small text-muted mb-0">No POs linked</p>
                              )}
                            </CardBody>
                          </Card>
                        </Col>
                      );
                    })
                    ) : (
                      <Col xs={12}>
                        <div className="text-center text-muted py-4">
                          {search ? "No sections match your search." : "No budget sections yet. Click \"Add Section\" to create one."}
                        </div>
                      </Col>
                    )}
                    </Row>
                    {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                      <small className="text-muted">
                        Showing {budgets.length} of {totalCount} section(s)
                      </small>
                      <div className="hstack gap-1">
                        <Button
                          color="light"
                          size="sm"
                          disabled={currentPage <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          Previous
                        </Button>
                        <span className="align-self-center small">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                          color="light"
                          size="sm"
                          disabled={currentPage >= totalPages}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                  </>
              )}
        </Col>
      </Row>

      {/* Bulk Import Modal */}
      <Modal isOpen={importModal} toggle={() => { setImportModal(false); setImportFile(null); setImportError(null); }} centered>
        <ModalHeader toggle={() => { setImportModal(false); setImportFile(null); setImportError(null); }}>
          Import Budget Sections
        </ModalHeader>
        <ModalBody>
          <Alert color="info" className="small">
            Upload a CSV or Excel file with columns:{" "}
            <strong>Section Name</strong>, <strong>Allocated Cost</strong>,{" "}
            <strong>Description</strong> (optional), <strong>Status</strong> (optional:
            draft / active / closed / over_budget). Multiple rows create multiple sections.
          </Alert>

          <div className="mb-3">
            <Button color="link" className="p-0" onClick={handleDownloadTemplate}>
              <i className="ri-download-2-line align-bottom me-1" />
              Download sample template
            </Button>
          </div>

          {importError && (
            <Alert color="danger" className="small">
              <i className="ri-error-warning-line align-bottom me-1" />
              {importError}
            </Alert>
          )}

          <FormGroup>
            <Label for="budget_import_file">Select File</Label>
            <Input
              id="budget_import_file"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e: any) => {
                setImportFile(e.target.files?.[0] || null);
                setImportError(null);
              }}
            />
          </FormGroup>
          {importFile && (
            <div className="small text-muted mb-2">
              <i className="ri-file-line me-1" />
              {importFile.name}
            </div>
          )}
          <div className="d-flex justify-content-end gap-2">
            <Button type="button" color="light" onClick={() => { setImportModal(false); setImportFile(null); setImportError(null); }}>
              Cancel
            </Button>
            <Button
              type="button"
              color="primary"
              disabled={!importFile || isImporting}
              onClick={handleBulkImport}
            >
              {isImporting ? "Importing..." : "Import"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Add Section Modal */}
      <Modal isOpen={addModal} toggle={() => setAddModal(false)} centered>
        <ModalHeader toggle={() => setAddModal(false)}>Add Budget Section</ModalHeader>
        <ModalBody>
          <Form onSubmit={addFormik.handleSubmit}>
            <FormGroup>
              <Label for="add_section_name">Section Name *</Label>
              <Input
                id="add_section_name"
                name="section_name"
                value={addFormik.values.section_name}
                onChange={addFormik.handleChange}
                onBlur={addFormik.handleBlur}
                invalid={addFormik.touched.section_name && !!addFormik.errors.section_name}
                placeholder="e.g. Electrical, Civil Works"
              />
              <FormFeedback>{addFormik.errors.section_name}</FormFeedback>
            </FormGroup>
            <FormGroup>
              <Label for="add_allocated_cost">Allocated Cost *</Label>
              <Input
                id="add_allocated_cost"
                name="allocated_cost"
                type="number"
                step="0.01"
                min="0"
                value={addFormik.values.allocated_cost}
                onChange={addFormik.handleChange}
                onBlur={addFormik.handleBlur}
                invalid={addFormik.touched.allocated_cost && !!addFormik.errors.allocated_cost}
                placeholder="0.00"
              />
              <FormFeedback>{addFormik.errors.allocated_cost as string}</FormFeedback>
            </FormGroup>
            <FormGroup>
              <Label for="add_description">Description</Label>
              <Input
                id="add_description"
                name="description"
                type="textarea"
                rows={2}
                value={addFormik.values.description}
                onChange={addFormik.handleChange}
                placeholder="Optional notes"
              />
            </FormGroup>
            <FormGroup>
              <Label for="add_status">Status</Label>
              <Input
                id="add_status"
                name="status"
                type="select"
                value={addFormik.values.status}
                onChange={addFormik.handleChange}
              >
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" color="light" onClick={() => setAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={isCreating}>
                {isCreating ? "Adding..." : "Add Section"}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* Edit Section Modal */}
      <Modal isOpen={editModal} toggle={() => { setEditModal(false); setSelectedBudget(null); }} centered>
        <ModalHeader toggle={() => { setEditModal(false); setSelectedBudget(null); }}>
          Edit Budget Section
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={editFormik.handleSubmit}>
            <FormGroup>
              <Label for="edit_section_name">Section Name *</Label>
              <Input
                id="edit_section_name"
                name="section_name"
                value={editFormik.values.section_name}
                onChange={editFormik.handleChange}
                onBlur={editFormik.handleBlur}
                invalid={editFormik.touched.section_name && !!editFormik.errors.section_name}
              />
              <FormFeedback>{editFormik.errors.section_name as string}</FormFeedback>
            </FormGroup>
            <FormGroup>
              <Label for="edit_allocated_cost">Allocated Cost *</Label>
              <Input
                id="edit_allocated_cost"
                name="allocated_cost"
                type="number"
                step="0.01"
                min="0"
                value={editFormik.values.allocated_cost}
                onChange={editFormik.handleChange}
                onBlur={editFormik.handleBlur}
                invalid={editFormik.touched.allocated_cost && !!editFormik.errors.allocated_cost}
              />
              <FormFeedback>{editFormik.errors.allocated_cost as string}</FormFeedback>
            </FormGroup>
            <FormGroup>
              <Label for="edit_description">Description</Label>
              <Input
                id="edit_description"
                name="description"
                type="textarea"
                rows={2}
                value={editFormik.values.description}
                onChange={editFormik.handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label for="edit_status">Status</Label>
              <Input
                id="edit_status"
                name="status"
                type="select"
                value={editFormik.values.status}
                onChange={editFormik.handleChange}
              >
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" color="light" onClick={() => { setEditModal(false); setSelectedBudget(null); }}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={isUpdating}>
                {isUpdating ? "Updating..." : "Update"}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* Delete confirmation */}
      <Modal isOpen={deleteModal} toggle={() => { setDeleteModal(false); setSelectedBudget(null); }} centered>
        <ModalHeader toggle={() => { setDeleteModal(false); setSelectedBudget(null); }}>
          Delete Section
        </ModalHeader>
        <ModalBody>
          <p>
            Remove budget section &quot;{selectedBudget?.section_name}&quot;? This cannot be undone.
          </p>
          <div className="d-flex justify-content-end gap-2">
            <Button color="light" onClick={() => { setDeleteModal(false); setSelectedBudget(null); }}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ProjectBudgetTab;
